import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { formatDate } from '@utils/date.ts';
import { PATHS } from '../../../router/paths.ts';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';
import { Eye } from 'lucide-react';
import { useGetAllSubscriptions } from '@utils/api/subscriptions/use-get-all-subscriptions.tsx';

type SubscriptionsRow = {
  id: number;
  sku: string;
  start_date: string;
  end_date: string;
  auto_renew: boolean;
  suspended: boolean;
  cancel_date: string;
  billing_channel_label: string;
};

const safeFormatDate = (d?: string | null) => {
  if (!d) return '-';
  const time = new Date(d).getTime();
  return Number.isNaN(time) ? '-' : formatDate(d);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapBillingChannel = (v: unknown, t: any) => {
  if (v === 1 || v === '1' || v === 'scriptix') return t('scriptix');
  if (v === 2 || v === '2' || v === 'azure') return t('azure');
  return String(v ?? '-');
};

export const Subscriptions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: subscriptions,
    isLoading,
    isFetching
  } = useGetAllSubscriptions();

  const rows: SubscriptionsRow[] = useMemo(() => {
    const list = subscriptions?.result ?? [];
    return list.map(s => ({
      id: s.id,
      sku: s.sku,
      start_date: safeFormatDate(s.start_date),
      end_date: safeFormatDate(s.end_date),
      auto_renew: s.auto_renew,
      suspended: s.suspended,
      cancel_date: safeFormatDate(s.cancel_date),
      billing_channel_label: mapBillingChannel(s.billing_channel, t)
    }));
  }, [subscriptions, t]);

  const columns = useMemo<DataGridColumn<SubscriptionsRow>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('ID'),
        maxWidth: 100,
        enableSorting: false,
        enableFiltering: false,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'sku',
        accessorKey: 'sku',
        header: t('sku'),
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'billing_channel',
        accessorKey: 'billing_channel_label',
        header: t('billing-channel'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'billing_channel',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('scriptix'), value: 'scriptix' },
          { label: t('azure'), value: 'azure' }
        ],
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'start_date',
        accessorKey: 'start_date',
        field: 'start_date',
        header: t('start-date'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'end_date',
        accessorKey: 'end_date',
        field: 'end_date',
        header: t('end-date'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'cancel_date',
        accessorKey: 'cancel_date',
        field: 'cancel_date',
        header: t('cancel-date'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'suspended',
        accessorKey: 'suspended',
        field: 'suspended',
        header: t('suspended'),
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'suspended',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => ((getValue() as boolean) ? t('yes') : t('no'))
      },
      {
        id: 'auto_renew',
        accessorKey: 'auto_renew',
        field: 'auto_renew',
        header: t('auto-renew'),
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'auto_renew',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => ((getValue() as boolean) ? t('yes') : t('no'))
      }
    ],
    [t]
  );

  const actions = useMemo<GridAction<SubscriptionsRow>[]>(
    () => [
      {
        id: 'view',
        label: t('view'),
        icon: <Eye className="h-4 w-4" />,
        intent: 'primary',
        run: row => navigate(PATHS(row.id.toString()).SUBSCRIPTION_CONTENT)
      }
    ],
    [navigate, t]
  );

  return (
    <div className="py-4">
      <DataGridV2<SubscriptionsRow>
        data={rows}
        columns={columns}
        actions={actions}
        enablePagination
        manualPagination
        enableSorting
        manualSorting
        enableFiltering
        manualFiltering
        totalItems={subscriptions ? subscriptions.total_results : undefined}
        pageSize={25}
        pageSizeOptions={[25, 50, 75, 100]}
        showColumnFilters={false}
        filterMode="popover"
        height="82vh"
        dense
        grid={false}
        striped
        loading={isLoading || isFetching || !subscriptions}
        getRowId={row => String(row.id)}
      />
    </div>
  );
};
